import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { Modal, Button } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';

import { spreads } from '../config/spreads';
import { Spread } from '../types/spread';
import { createCheckoutSession } from '../utils/checkout';
import OrderSummary from '../components/order/OrderSummary';
import ExpressOption from '../components/order/ExpressOption';

const monthsBg = [
  'Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни',
  'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември',
];

function getMonthOptions(): { value: string; label: string }[] {
  const now = new Date();
  const startMonth = now.getMonth() + 1;
  const startYear = now.getFullYear();

  return Array.from({ length: 12 }, (_, i) => {
    const monthIndex = (startMonth + i) % 12;
    const yearOffset = Math.floor((startMonth + i) / 12);
    const year = startYear + yearOffset;
    const month = String(monthIndex + 1).padStart(2, '0');
    return { value: `${year}-${month}`, label: `${monthsBg[monthIndex]} ${year}` };
  });
}

const formatLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getSlotsForDate = (spread: Spread | undefined, date: Date | undefined): string[] => {
  if (!spread?.bookingSchedule || !date) return [];
  return spread.bookingSchedule.find((d) => d.dayOfWeek === date.getDay())?.slots ?? [];
};

const isDateBookable = (spread: Spread | undefined, date: Date): boolean =>
  Boolean(spread?.bookingSchedule?.some((d) => d.dayOfWeek === date.getDay()));

const getMaxExampleQuestions = (spread: Spread, isSingleQuestion: boolean): number | undefined => {
  const match = spread.inquiryPlaceholder.match(/до\s+(\d+)\s+въпрос/);
  if (match) return parseInt(match[1], 10);
  return isSingleQuestion ? 1 : undefined;
};

const shuffle = <T,>(items: T[]): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const parseInquiryLines = (text: string): string[] =>
  text.split('\n').map((line) => line.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);

const isOnlyExampleQuestions = (values: string[], pool: string[] | undefined): boolean => {
  const nonEmpty = values.map((v) => v.trim()).filter(Boolean);
  if (!nonEmpty.length || !pool?.length) return false;
  return nonEmpty.every((v) => pool.includes(v));
};

const Order: React.FC = () => {
  const monthOptions = getMonthOptions();

  const [minDate, setMinDate] = useState<Date>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const { slug } = useParams<{ slug: string }>();
  const spread = spreads.find((s) => s.href === slug);

  const isMonthlySpread = slug === 'mesechna-taro-prognoza';
  const isSingleQuestion = slug === 'horaren-vapros';
  const isTherapy = Boolean(spread?.bookingSchedule);
  const therapyTimeSlots = getSlotsForDate(spread, selectedDate);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [inquiry, setInquiry] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [questions, setQuestions] = useState<string[]>(['']);
  const [checked, setChecked] = useState(false);
  const [express, setExpress] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showExampleQuestionsModal, setShowExampleQuestionsModal] = useState(false);

  useEffect(() => {
    if (isMonthlySpread) {
      setSelectedDate(new Date(monthOptions[0].value));
      return;
    }

    if (isTherapy) {
      const twoDays = new Date();
      twoDays.setDate(twoDays.getDate() + 2);
      setMinDate(twoDays);
      setSelectedDate(undefined);
      setSelectedTime('');
      return;
    }

    const getFutureDate = (daysAhead: number) => {
      const date = new Date();
      date.setDate(date.getDate() + daysAhead);
      return date;
    };

    const futureDate = getFutureDate(express ? 1 : 4);
    setMinDate(futureDate);
    setSelectedDate((prev) => (!prev || prev < futureDate ? futureDate : prev));
  }, [slug, express]);

  if (!spread) {
    return (
      <div className="container mb-5" style={{ paddingTop: 120 }}>
        <p>
          Подредбата не беше намерена. <a href="/">Избери друга</a>
        </p>
      </div>
    );
  }

  const calculatedPrice = isSingleQuestion
    ? spread.price * questions.length
    : express
      ? parseFloat((spread.price * 1.5).toFixed(2))
      : spread.price;

  const applyExampleQuestions = () => {
    if (!spread.exampleQuestions?.length) return;
    const max = getMaxExampleQuestions(spread, isSingleQuestion);
    const selected = shuffle(spread.exampleQuestions).slice(0, max ?? spread.exampleQuestions.length);
    if (isSingleQuestion) {
      setQuestions(selected);
    } else {
      setInquiry(selected.map((q, i) => `${i + 1}. ${q}`).join('\n'));
    }
  };

  const handleLoadExampleQuestionsClick = () => {
    const currentValues = isSingleQuestion ? questions : parseInquiryLines(inquiry);
    const hasContent = currentValues.some((v) => v.trim());
    const isUnchangedExamples = isOnlyExampleQuestions(currentValues, spread.exampleQuestions);
    if (hasContent && !isUnchangedExamples) {
      setShowExampleQuestionsModal(true);
    } else {
      applyExampleQuestions();
    }
  };

  const confirmLoadExampleQuestions = () => {
    applyExampleQuestions();
    setShowExampleQuestionsModal(false);
  };

  const addQuestion = () => setQuestions((prev) => [...prev, '']);
  const removeQuestion = (index: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  const updateQuestion = (index: number, value: string) =>
    setQuestions((prev) => prev.map((q, i) => (i === index ? value : q)));

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!email) {
      newErrors.email = 'Задължително поле';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Невалиден email';
    }

    if (!confirmEmail) {
      newErrors.confirmEmail = 'Задължително поле';
    } else if (!/\S+@\S+\.\S+/.test(confirmEmail)) {
      newErrors.confirmEmail = 'Невалиден email';
    }

    if (email !== confirmEmail) {
      newErrors.confirmEmail = 'Email адресите не съвпадат';
    }

    if (!name) newErrors.name = 'Задължително поле';

    if (isTherapy) {
      const digits = phone.replace(/\D/g, '');
      if (!phone.trim()) {
        newErrors.phone = 'Задължително поле';
      } else if (digits.length < 7 || digits.length > 15) {
        newErrors.phone = 'Невалиден телефонен номер';
      }
      if (!selectedDate) {
        newErrors.date = 'Задължително поле';
      } else if (!isDateBookable(spread, selectedDate)) {
        newErrors.date = 'Изберете дата от наличните за резервация';
      } else if (minDate) {
        const toDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
        if (toDay(selectedDate) < toDay(minDate)) newErrors.date = 'Невалидна дата';
      }
      if (!selectedTime) {
        newErrors.time = 'Изберете час';
      } else if (!getSlotsForDate(spread, selectedDate).includes(selectedTime)) {
        newErrors.time = 'Невалиден час';
      }
    } else {
      if (isSingleQuestion) {
        if (questions.some((q) => !q.trim()))
          newErrors.inquiry = 'Всички въпроси трябва да бъдат попълнени';
      } else {
        if (!inquiry) newErrors.inquiry = 'Задължително поле';
      }
      if (!selectedDate) newErrors.date = 'Задължително поле';
    }

    if (!checked) newErrors.checked = 'Съгласието с общите условия е задължително';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.MouseEvent) => {
    window.gtag_report_conversion?.();
    e.preventDefault();
    if (validate()) {
      createCheckoutSession({
        priceId: spread.priceId,
        name,
        email,
        date: isTherapy
          ? `${formatLocalDate(selectedDate!)} ${selectedTime}`
          : formatLocalDate(selectedDate ?? minDate ?? new Date()),
        inquiry: isTherapy ? phone : (isSingleQuestion ? questions.join('\n\n') : inquiry),
        isExpress: express,
        quantity: isSingleQuestion ? questions.length : undefined,
      });
    }
  };

  return (
    <div className="container mb-5" style={{ paddingTop: 120 }}>
      <section className="position-relative mx-auto" style={{ maxWidth: 1024 }}>
        <div className="mb-4">
          <h1 className="fw-bold mb-4 text-start">
            <strong>Данни за поръчката</strong>
          </h1>
          <hr />
          <a className="fs-6 d-block text-start" href="/">
            Избери друга
          </a>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icon-tabler-cards position-absolute"
            style={{
              right: 0,
              top: -5,
              opacity: 0.3,
              fontSize: 64,
              color: 'var(--bs-secondary)',
              zIndex: -1,
              transform: 'rotate(30deg)',
            }}
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M3.604 7.197l7.138 -3.109a.96 .96 0 0 1 1.27 .527l4.924 11.902a1 1 0 0 1 -.514 1.304l-7.137 3.109a.96 .96 0 0 1 -1.271 -.527l-4.924 -11.903a1 1 0 0 1 .514 -1.304z" />
            <path d="M15 4h1a1 1 0 0 1 1 1v3.5" />
            <path d="M20 6c.264 .112 .52 .217 .768 .315a1 1 0 0 1 .53 1.311l-2.298 5.374" />
          </svg>
        </div>

        <div>
          <form className="fw-bold text-start">
            <div className="row mb-4">
              <div className="col-md-5">
                <label className="form-label m-0">
                  Име<span className="text-danger">*</span>
                  {errors.name && (
                    <span className="text-danger ms-2">{errors.name}</span>
                  )}
                </label>
                <input
                  className={`bg-white shadow-sm form-control form-control-lg mb-3 ${
                    errors.name ? 'border-danger' : ''
                  }`}
                  type="text"
                  name="name"
                  placeholder="Име"
                  onChange={(e) => setName(e.target.value)}
                />
                <label className="form-label m-0">
                  Email адрес<span className="text-danger">*</span>
                  {errors.email && (
                    <span className="text-danger ms-2">{errors.email}</span>
                  )}
                </label>
                <input
                  className={`bg-white shadow-sm form-control form-control-lg mb-3 ${
                    errors.email ? 'border-danger' : ''
                  }`}
                  type="text"
                  name="email"
                  placeholder="Email адрес"
                  onChange={(e) => setEmail(e.target.value)}
                />
                <label className="form-label mb-0">
                  Потвърди email адрес<span className="text-danger">*</span>
                  {errors.confirmEmail && (
                    <span className="text-danger ms-2">
                      {errors.confirmEmail}
                    </span>
                  )}
                </label>
                <input
                  className={`bg-white shadow-sm form-control form-control-lg ${
                    errors.confirmEmail ? 'border-danger' : ''
                  }`}
                  type="email"
                  name="confirmEmail"
                  placeholder="Потвърди email адрес"
                  onChange={(e) => setConfirmEmail(e.target.value)}
                />
              </div>
              <div className="col-md-7 d-flex flex-column mt-3 mt-md-0">
                {isTherapy ? (
                  <>
                    <label className="form-label m-0">
                      Телефонен номер<span className="text-danger">*</span>
                      {errors.phone && (
                        <span className="text-danger ms-2">{errors.phone}</span>
                      )}
                    </label>
                    <input
                      className={`bg-white shadow-sm form-control form-control-lg mb-3 ${
                        errors.phone ? 'border-danger' : ''
                      }`}
                      type="tel"
                      placeholder="+359 88 888 8888"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />

                    <label className="form-label m-0">
                      Дата за резервация<span className="text-danger">*</span>
                      {errors.date && (
                        <span className="text-danger ms-2">{errors.date}</span>
                      )}
                    </label>
                    <DatePicker calendarStartDay={1}
                      className={`bg-white shadow-sm form-control form-control-lg mb-3 ${
                        errors.date ? 'border-danger' : ''
                      }`}
                      selected={selectedDate}
                      onChange={(date) => {
                        setSelectedDate(date as Date);
                        setSelectedTime('');
                      }}
                      name="date-time-picker"
                      minDate={minDate}
                      filterDate={(date) => isDateBookable(spread, date)}
                      placeholderText="Изберете дата за резервация"
                      dateFormat="dd.MM.yyyy"
                      onChangeRaw={(e) => e?.preventDefault()}
                    />

                    {selectedDate && (
                      <div>
                        <label className="form-label m-0">
                          Час<span className="text-danger">*</span>
                          {errors.time && (
                            <span className="text-danger ms-2">{errors.time}</span>
                          )}
                        </label>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {therapyTimeSlots.map((slot) => (
                            <button
                              key={slot}
                              type="button"
                              className={`btn fw-bold ${
                                selectedTime === slot
                                  ? 'btn-primary'
                                  : 'btn-outline-primary'
                              }`}
                              style={{ minWidth: 72 }}
                              onClick={() => setSelectedTime(slot)}
                            >
                              {slot}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <label className="form-label m-0">
                      {isSingleQuestion ? 'Вашите въпроси' : 'Вашето запитване'}
                      <span className="text-danger">*</span>
                      {errors.inquiry && (
                        <span className="text-danger ms-2">{errors.inquiry}</span>
                      )}
                    </label>
                    {isSingleQuestion ? (
                      <div className="mb-3">
                        {questions.map((q, i) => (
                          <div key={i} className="d-flex gap-2 mb-2">
                            <textarea
                              className={`bg-white shadow-sm form-control form-control-lg ${
                                errors.inquiry && !q.trim() ? 'border-danger' : ''
                              }`}
                              placeholder={
                                i === 0
                                  ? spread.inquiryPlaceholder
                                  : `Въпрос ${i + 1}: Напр. Какво да очаквам в следващите месеци?`
                              }
                              style={{ minHeight: 80 }}
                              value={q}
                              onChange={(e) => updateQuestion(i, e.target.value)}
                            />
                            {questions.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-danger rounded-circle align-self-center d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 32, height: 32, color: '#fff' }}
                                onClick={() => removeQuestion(i)}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none fw-semibold"
                          onClick={addQuestion}
                        >
                          + добави въпрос
                        </button>
                        <div className="text-muted fw-normal small mt-1">
                          +{spread.price} EUR за всеки допълнителен въпрос
                        </div>
                      </div>
                    ) : (
                      <textarea
                        className={`bg-white shadow-sm form-control form-control-lg flex-fill mb-3 ${
                          errors.inquiry ? 'border-danger' : ''
                        }`}
                        placeholder={spread.inquiryPlaceholder}
                        style={{ minHeight: 136 }}
                        value={inquiry}
                        onChange={(e) => setInquiry(e.target.value)}
                      />
                    )}
                    {Boolean(spread.exampleQuestions?.length) && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm align-self-start mb-3"
                        onClick={handleLoadExampleQuestionsClick}
                      >
                        Зареди примерни въпроси
                      </button>
                    )}
                    <label className="form-label m-0">
                      {isMonthlySpread ? 'Месец за прогнозата' : 'Дата за получаване'}
                      <span className="text-danger">*</span>
                    </label>

                    {isMonthlySpread ? (
                      <select
                        id="date"
                        onChange={(e) =>
                          setSelectedDate(new Date(e.target.value))
                        }
                        className="bg-white shadow-sm form-control form-control-lg flex-fill mb-3"
                      >
                        {monthOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <DatePicker calendarStartDay={1}
                        id="date"
                        className="bg-white shadow-sm form-control form-control-lg"
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date as Date)}
                        minDate={minDate}
                        placeholderText="Choose a date"
                        dateFormat="yyyy-MM-dd"
                        onChangeRaw={(e) => e?.preventDefault()}
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            <OrderSummary
              spread={spread}
              calculatedPrice={calculatedPrice}
              express={express}
              isSingleQuestion={isSingleQuestion}
              questionsCount={questions.length}
              isTherapy={isTherapy}
            />

            {errors.checked && (
              <p className="text-danger m-0 fs-6">{errors.checked}</p>
            )}
            <div className="form-check form-control-lg">
              <input
                className="form-check-input border-primary"
                type="checkbox"
                id="formCheck-2"
                onChange={(e) => setChecked(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="formCheck-2">
                Съгласих се с&nbsp;
                <a href="/terms-of-service">общите условия</a>
                <span className="text-danger">*</span>
              </label>
            </div>

            {spread.isExpressEligible && (
              <ExpressOption
                spread={spread}
                calculatedPrice={calculatedPrice}
                checked={express}
                onChange={setExpress}
              />
            )}

            <div className="row mt-3">
              <div className="col-md-12">
                <button
                  className="btn btn-primary w-100 py-3 fw-bold"
                  type="button"
                  onClick={handleSubmit}
                >
                  {isTherapy ? 'Запази час' : 'Поръчай'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Modal
        show={showExampleQuestionsModal}
        onHide={() => setShowExampleQuestionsModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">Презаписване на въпросите</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Сигурни ли сте, че искате да презапишете въведеното от Вас съдържание с примерни
          въпроси?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowExampleQuestionsModal(false)}
          >
            Отказ
          </Button>
          <Button variant="primary" onClick={confirmLoadExampleQuestions}>
            Презапиши
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Order;
